import { useQuery } from "@tanstack/react-query";

import { apiInstance } from "@shared/services";
import { convertObjectToCamelCase, handleApiError } from "@shared/helpers";
import i18nPayfac from "@shared/configs/i18n/i18n";

import { PAYFAC_QUERIES } from "./queries";

import type {
  IRatePlanHistoryWithPagination,
  IRatePlanHistoryDetail,
} from "@payfac/types";

const URLS = {
  ratePlanHistory: (planId: number | string) =>
    `/v2/payfac/rate-plans/${planId}/history`,
  ratePlanHistoryDetail: (planId: number | string, historyId: number | string) =>
    `/v2/payfac/rate-plans/${planId}/history/${historyId}`,
};

const api = apiInstance({
  enableBearerToken: true,
  enableXTokenApi: true,
});

export const useGetRatePlansHistory = ({
  planId,
  filters,
}: {
  planId: number;
  filters?: {
    limit?: number;
    page?: number;
  };
}) => {
  const getRatePlansHistory = async (): Promise<{
    success: boolean;
    message: string;
    data?: IRatePlanHistoryWithPagination;
  }> => {
    try {
      const { data } = await api.get(URLS.ratePlanHistory(planId), {
        params: filters,
      });

      return {
        success: true,
        message: i18nPayfac.t("payfac.serviceRatePlanHistoryListSuccess"),
        data: convertObjectToCamelCase(data) as IRatePlanHistoryWithPagination,
      };
    } catch (error: any) {
      return handleApiError(
        error,
        i18nPayfac.t("payfac.serviceRatePlanHistoryListError"),
      );
    }
  };

  return useQuery({
    queryKey: [PAYFAC_QUERIES.RATE_PLANS_HISTORY, planId, filters],
    queryFn: () => getRatePlansHistory(),
    enabled: !!planId,
  });
};

export const useGetRatePlanHistoryDetail = ({
  planId,
  historyId,
}: {
  planId: number;
  historyId: number | null;
}) => {
  const getRatePlanHistoryDetail = async (): Promise<{
    success: boolean;
    message: string;
    data?: IRatePlanHistoryDetail;
  }> => {
    try {
      const { data } = await api.get(
        URLS.ratePlanHistoryDetail(planId, historyId as number),
      );

      return {
        success: true,
        message: i18nPayfac.t("payfac.serviceRatePlanHistoryDetailSuccess"),
        data: convertObjectToCamelCase(data) as IRatePlanHistoryDetail,
      };
    } catch (error: any) {
      return handleApiError(
        error,
        i18nPayfac.t("payfac.serviceRatePlanHistoryDetailError"),
      );
    }
  };

  return useQuery({
    queryKey: [PAYFAC_QUERIES.RATE_PLANS_HISTORY_DETAIL, planId, historyId],
    queryFn: () => getRatePlanHistoryDetail(),
    enabled: !!planId && !!historyId,
  });
};
